import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';

const ROW_LIMIT = 500;

export function useDbExplorerTables() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/db-explorer/tables')
      .then((res) => {
        if (cancelled) return;
        setTables(res.data || []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { tables, loading, error };
}

/**
 * Per-table state + data fetching. Parent must remount with `key={schema}.${table}`
 * so all state naturally resets when the user picks a different table.
 */
export function useTableData(schema, table) {
  const [columns, setColumns] = useState([]);
  const [columnTypes, setColumnTypes] = useState({});
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [sort, setSort] = useState({ column: null, direction: 'asc' });
  const [offset, setOffset] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);

  // fetch columns when schema/table is set
  useEffect(() => {
    if (!schema || !table) return undefined;
    let cancelled = false;
    api.get(`/api/db-explorer/tables/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/columns`)
      .then((res) => {
        if (cancelled) return;
        const cols = res.data || [];
        setColumns(cols.map((c) => c.name));
        setColumnTypes(Object.fromEntries(cols.map((c) => [c.name, c])));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
      });
    return () => { cancelled = true; };
  }, [schema, table]);

  // fetch rows whenever filter/sort/global/offset/manual-refresh changes
  useEffect(() => {
    if (!schema || !table) return undefined;
    let cancelled = false;
    const handle = setTimeout(() => {
      api.post(
        `/api/db-explorer/tables/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/rows`,
        {
          filters,
          sort_column: sort.column,
          sort_direction: sort.direction,
          global_filter: globalFilter || null,
          limit: ROW_LIMIT,
          offset,
          include_count: true,
        },
      )
        .then((res) => {
          if (cancelled) return;
          setColumns(res.data.columns || []);
          setRows(res.data.rows || []);
          setTotal(res.data.total);
          setError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err?.response?.data?.detail || err.message);
          setRows([]);
        })
        .finally(() => { if (!cancelled) setLoading(false); });

      // Begin loading state asynchronously (outside sync effect body)
      Promise.resolve().then(() => { if (!cancelled) setLoading(true); });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [schema, table, filters, sort, globalFilter, offset, refreshTick]);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  const setColumnFilter = useCallback((col, value) => {
    setOffset(0);
    setFilters((prev) => {
      const next = { ...prev };
      if (!value || value.trim() === '') {
        delete next[col];
      } else {
        next[col] = value;
      }
      return next;
    });
  }, []);

  const toggleSort = useCallback((col) => {
    setOffset(0);
    setSort((prev) => {
      if (prev.column === col) {
        if (prev.direction === 'asc') return { column: col, direction: 'desc' };
        return { column: null, direction: 'asc' };
      }
      return { column: col, direction: 'asc' };
    });
  }, []);

  const setGlobal = useCallback((v) => {
    setOffset(0);
    setGlobalFilter(v);
  }, []);

  const pageInfo = useMemo(() => ({
    offset,
    limit: ROW_LIMIT,
    total,
    showingFrom: rows.length === 0 ? 0 : offset + 1,
    showingTo: offset + rows.length,
  }), [offset, total, rows.length]);

  const goNext = useCallback(() => {
    if (total != null && offset + ROW_LIMIT >= total) return;
    setOffset((o) => o + ROW_LIMIT);
  }, [offset, total]);

  const goPrev = useCallback(() => {
    setOffset((o) => Math.max(0, o - ROW_LIMIT));
  }, []);

  const goFirst = useCallback(() => setOffset(0), []);
  const goLast = useCallback(() => {
    if (total == null) return;
    const last = Math.max(0, Math.floor((total - 1) / ROW_LIMIT) * ROW_LIMIT);
    setOffset(last);
  }, [total]);

  return {
    columns, columnTypes, rows, total, loading, error,
    filters, globalFilter, sort,
    setColumnFilter, setGlobal, toggleSort,
    refresh, pageInfo, goNext, goPrev, goFirst, goLast,
  };
}

export async function exportCsv({ schema, table, filters, sort, globalFilter }) {
  const res = await api.post(
    `/api/db-explorer/tables/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/export`,
    {
      filters,
      sort_column: sort?.column || null,
      sort_direction: sort?.direction || 'asc',
      global_filter: globalFilter || null,
      limit: 100000,
      offset: 0,
      include_count: false,
    },
    { responseType: 'blob' },
  );
  const blob = new Blob([res.data], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${schema}_${table}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
